// src/pages/Games.tsx

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../utils/apiBase';
import {
  fetchGamesCatalog,
  CREDIT_PERIOD_LABELS,
  type GameInfo,
} from '../utils/gameCatalog';
import { fetchCreditBalance } from '../utils/creditsApi';

const Games: React.FC = () => {
  const { token, isAdmin, isAuthenticated } = useAuth();
  const [games, setGames] = useState<GameInfo[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [launchingGameId, setLaunchingGameId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const catalogData = await fetchGamesCatalog(token);
      setGames(catalogData);
      if (token) {
        const bal = await fetchCreditBalance(token);
        setBalance(bal);
      }
      setLoading(false);
    };

    loadData();
  }, [token]);

  const startGameSession = async (gameId: string, baseUrl: string) => {
    if (!token) {
      window.location.href = `/login?redirect=${encodeURIComponent('/games')}`;
      return;
    }

    setLaunchingGameId(gameId);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/games/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ game: gameId }),
      });

      if (!response.ok) {
        // 402 Payment Required — the backend couldn't debit the balance.
        // The credit system already launches the game free when the user
        // has enough credits or an active credit session, so a 402 here
        // means they genuinely need to top up. Point them at the Credits
        // page instead of the legacy pass/pack purchase flow.
        if (response.status === 402) {
          const body = await response.json().catch(() => ({}));
          setError(
            `You need ${body.required ?? ''} credits to launch this game. Head to the Credits page to top up.`,
          );
          return;
        }
        throw new Error('Unable to start session');
      }

      const data = await response.json();
      const sessionToken = data.token;
      const gameUrl = `${baseUrl}?token=${encodeURIComponent(sessionToken)}`;
      window.open(gameUrl, '_blank', 'noopener,noreferrer');

      // If credits were just charged, refresh the balance display.
      if (data.launch?.charged && token) {
        const fresh = await fetchCreditBalance(token);
        setBalance(fresh);
      }
    } catch (err) {
      console.error(err);
      setError('Unable to start the game session. Please try again.');
    } finally {
      setLaunchingGameId(null);
    }
  };

  if (loading) {
    return (
      <div className="section container" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>Loading games...</p>
      </div>
    );
  }

  return (
    <div>
      <section className="section bg-light">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1>Snowbird Industries Games</h1>
              <p className="text-secondary text-large">
                One Snowbird Industries account gives you access to every game in our catalog.
                Join games for free, and spend credits only when you launch.
              </p>
            </div>
            {isAuthenticated && balance !== null && (
              <Link
                to="/credits"
                className="card"
                style={{
                  padding: '0.85rem 1.25rem',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  minWidth: '160px',
                }}
                title="View credits"
              >
                <span className="text-secondary" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Credit Balance
                </span>
                <strong style={{ fontSize: '1.4rem', marginTop: '0.25rem' }}>{balance.toFixed(0)}</strong>
                <span className="text-secondary" style={{ fontSize: '0.7rem' }}>Tap to buy more</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>Featured Games</h2>
          {error && (
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <span>{error}</span>
              <Link to="/credits" className="btn btn-primary">Go to Credits</Link>
            </div>
          )}
          {games.length === 0 ? (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <p className="text-secondary">No games available at this time.</p>
            </div>
          ) : (
            <div className="grid grid-2">
              {games.map((game) => (
                <div key={game.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0 }}>{game.name}</h3>
                      {isAdmin && game.status && game.status !== 'active' && (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            backgroundColor: game.status === 'beta' ? '#fef3c7' : '#f3f4f6',
                            color: game.status === 'beta' ? '#92400e' : '#6b7280',
                          }}
                        >
                          {game.status.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      )}
                      {game.creditCost > 0 ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.55rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            backgroundColor: '#eef2ff',
                            color: '#4338ca',
                          }}
                          title={`${game.creditCost} credits ${CREDIT_PERIOD_LABELS[game.creditPeriod].toLowerCase()}`}
                        >
                          {game.creditCost} credits / {game.creditPeriod}
                        </span>
                      ) : (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.55rem',
                            borderRadius: '9999px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                          }}
                        >
                          Free
                        </span>
                      )}
                    </div>
                    <p className="text-secondary">{game.description}</p>
                  </div>
                  {game.image && (
                    <img
                      src={game.image}
                      alt={`${game.name} cover`}
                      style={{ width: '25%', minWidth: '160px', maxWidth: '260px', borderRadius: '8px' }}
                    />
                  )}
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => startGameSession(game.id, game.launchUrl)}
                      disabled={launchingGameId === game.id}
                    >
                      {launchingGameId === game.id ? 'Launching...' : `Launch ${game.name}`}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section bg-white">
        <div className="container">
          <h2>How Game Credits Work</h2>
          <p className="text-secondary">
            Launching a game spends credits based on that game's rate. Once you've paid for a
            period, you can re-launch that game free until the period ends — no need to pay again.
            Joining a game someone else launched is always free.
          </p>
          <p className="text-secondary mt-3">
            Top up your balance anytime on the{' '}
            <Link to="/credits">Credits page</Link>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Games;
